/**
 * CategoryListPage.js - 商品分类管理页
 * 支持新建、编辑、删除分类，通过 Modal 弹窗进行表单操作
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Popconfirm,
  message,
  Typography,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { adminProductApi } from '../../api/index';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export default function CategoryListPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  // Modal 可见性
  const [modalVisible, setModalVisible] = useState(false);
  // 当前正在编辑的分类（null 表示新建）
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form] = Form.useForm();

  // 拉取分类列表
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminProductApi.listCategories();
      setCategories(Array.isArray(data) ? data : data?.list || []);
    } catch (err) {
      message.error('加载分类列表失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // 打开新建 Modal
  const handleCreate = () => {
    setEditingCategory(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 打开编辑 Modal 并回填数据
  const handleEdit = (record) => {
    setEditingCategory(record);
    form.setFieldsValue({
      name: record.name,
      slug: record.slug,
      description: record.description,
      imageUrl: record.imageUrl,
      sortOrder: record.sortOrder ?? 0,
      parentId: record.parentId || undefined,
    });
    setModalVisible(true);
  };

  // 关闭 Modal
  const handleModalClose = () => {
    setModalVisible(false);
    setEditingCategory(null);
    form.resetFields();
  };

  // 提交表单（新建或更新）
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (editingCategory) {
        // 更新已有分类
        await adminProductApi.updateCategory(editingCategory.id, values);
        message.success('分类更新成功');
      } else {
        // 创建新分类
        await adminProductApi.createCategory(values);
        message.success('分类创建成功');
      }

      handleModalClose();
      fetchCategories();
    } catch (err) {
      if (err?.errorFields) return; // 表单验证错误
      message.error('保存失败：' + (err.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  // 删除分类
  const handleDelete = async (id) => {
    try {
      await adminProductApi.deleteCategory(id);
      message.success('分类已删除');
      fetchCategories();
    } catch (err) {
      message.error('删除失败：' + (err.message || '未知错误'));
    }
  };

  // 根据分类 name 自动生成 slug（简单转小写并替换空格）
  const handleNameChange = (e) => {
    const val = e.target.value;
    // 如果 slug 字段还未手动修改，自动生成
    const currentSlug = form.getFieldValue('slug');
    if (!currentSlug || !editingCategory) {
      const slug = val
        .toLowerCase()
        .replace(/[\s_]+/g, '-')
        .replace(/[^a-z0-9\-\u4e00-\u9fa5]/g, '');
      form.setFieldValue('slug', slug);
    }
  };

  // 表格列配置
  const columns = [
    {
      title: '分类名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span>
          {record.parentId && <span style={{ color: '#aaa', marginRight: 8 }}>└</span>}
          <strong>{text}</strong>
        </span>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      render: (text) => (
        <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#666' }}>
          {text}
        </span>
      ),
    },
    {
      title: '排序值',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 90,
      align: 'center',
    },
    {
      title: '父分类',
      dataIndex: 'parentId',
      key: 'parentId',
      width: 130,
      render: (parentId) => {
        if (!parentId) return <span style={{ color: '#aaa' }}>顶级分类</span>;
        const parent = categories.find((c) => c.id === parentId);
        return parent?.name || parentId;
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Popconfirm
          title="确认删除该分类？"
          description="删除后数据无法恢复，请确保该分类下没有商品。"
          onConfirm={() => handleDelete(record.id)}
          okText="确认删除"
          cancelText="取消"
          okButtonProps={{ danger: true }}
        >
          <Tooltip title="删除">
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          分类管理
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建分类
        </Button>
      </div>

      <Card style={{ borderRadius: 8 }} bodyStyle={{ padding: 0 }}>
        <Table
          dataSource={categories}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 50, showTotal: (t) => `共 ${t} 条` }}
          locale={{ emptyText: '暂无分类数据' }}
          scroll={{ x: 600 }}
          onRow={(record) => ({
            onClick: () => handleEdit(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {/* 新建/编辑 Modal */}
      <Modal
        title={editingCategory ? '编辑分类' : '新建分类'}
        open={modalVisible}
        onCancel={handleModalClose}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        width={560}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ sortOrder: 0 }}
          style={{ marginTop: 8 }}
        >
          <Form.Item
            name="name"
            label="分类名称"
            rules={[{ required: true, message: '请输入分类名称' }]}
          >
            <Input placeholder="输入分类名称" onChange={handleNameChange} />
          </Form.Item>

          <Form.Item
            name="slug"
            label="Slug（URL 标识）"
            rules={[{ required: true, message: '请输入 Slug' }]}
          >
            <Input
              placeholder="url-friendly-slug"
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item name="description" label="描述">
            <TextArea rows={3} placeholder="分类描述（可选）" />
          </Form.Item>

          <Form.Item name="imageUrl" label="封面图 URL">
            <Input placeholder="https://example.com/image.jpg" />
          </Form.Item>

          <Form.Item name="parentId" label="父分类">
            <Select
              placeholder="选择父分类（顶级分类可不选）"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {categories
                .filter((c) => c.id !== editingCategory?.id) // 排除自身
                .map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item name="sortOrder" label="排序值（数值越小越靠前）">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
